import {
  FeatureName,
  useCurrentAccount,
  useCurrentWorkspace,
  useFeatures,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
import { SearchIcon, SearchInput, tokens } from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Navigate } from 'react-router-dom';
import { useHomeDashboardFilters } from '../hooks/useHomeDashboardFilters';
import { DEFAULT_TIME_RANGE, TimeRange } from '../types';
import { ActivityMap } from './activityMap';
import { CardStrip } from './cardStrip';
import { FilterBadge } from './filterBadge';
import { PulseDashboard } from './pulseDashboard';
import { PulseFilterPanel } from './pulseFilterPanel';
import { TimeRangeSelector } from './timeRangeSelector';

/**
 * HomeDashboardPage — top-level shell for the workspace home dashboard.
 *
 * Branches by workspace type. Both branches share the same landing/self-guard
 * contract: ProtectedRoute only enforces `requiredPermission`, NOT the feature
 * flag or workspace type — so this page self-guards (covers direct-URL access),
 * redirecting to /activities when no flag/type combination matches:
 *  - OEM workspace + `OemHomeDashboard` + Crop Circle → the OEM 6-card body.
 *  - Producer workspace + `PulseHomeDashboard` + Crop Circle → `<PulseDashboard/>`.
 *  - anything else → /activities.
 *
 * No bounce while `useFeatures()` is still loading: `useIsFeatureEnabled` reads
 * false until features resolve, so a direct /home hit or refresh would otherwise
 * bounce even when the flag is on. Mirror app/home.tsx's landing gate — wait for
 * BOTH workspace and features before deciding.
 */
export const HomeDashboardPage = () => {
  // storageOnly=true to match the nav gate (useMenuPermission) exactly: a global
  // admin who hasn't selected/impersonated a workspace resolves to null here,
  // instead of falling back to their *home* account.workspace. Without this, such
  // an admin (Producer home workspace + Pulse flag on) renders the Pulse body at
  // /home while the nav — which uses storageOnly — shows no Home item. Aligning
  // the resolution keeps the page, the landing gate, and the nav in agreement.
  const workspace = useCurrentWorkspace(true);
  const isOemHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.OemHomeDashboard
  );
  const isPulseHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.PulseHomeDashboard
  );
  // Crop Circle is a precondition for both dashboards (see home.tsx landing): the
  // glass/tokenized chrome assumes the CC skin. Mirror the same gate here so a
  // direct-URL hit with CC off bounces instead of rendering a broken page under
  // the legacy Header.
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // Features must resolve before the self-guard can trust useIsFeatureEnabled:
  // it reads false while useFeatures() is still loading, so a direct /home hit
  // or refresh would otherwise bounce to /activities even when the flag is on.
  // Mirror app/home.tsx's landing gate — wait for the account AND features.
  const { account, isFetching: accountFetching } = useCurrentAccount();
  const { data: features, isLoading: featuresLoading } = useFeatures();

  // Hold (neutral placeholder, no redirect) while the account or features are
  // still resolving, then commit. Two things must be settled before trusting a
  // falsy workspace as a redirect signal:
  //  - Account: gate on `accountFetching`, not just the initial load. With
  //    storageOnly=true, useCurrentWorkspace(true) returns null for the WHOLE
  //    duration of ANY account fetch — including a background refetch (cache
  //    expiry / explicit invalidation) where `account` is still cached. Treating
  //    that transient null as terminal would bounce a valid Producer off the
  //    dashboard to /activities mid-session. Waiting on accountFetching makes the
  //    redirect fire only once the account is settled, so a remaining null is
  //    genuinely terminal (global admin who hasn't selected a workspace).
  //  - Features: useIsFeatureEnabled reads false until features load.
  // Once settled, a falsy workspace falls through to the redirect below — we do
  // NOT keep waiting on `workspace` itself (the trap home.tsx warns about: a
  // global admin's null workspace is terminal and would strand them forever). We
  // render the placeholder rather than a body during this window because falling
  // through to <OemDashboard/> briefly flashed the WRONG dashboard (the 6-card
  // OEM strip, with OEM-only Products/Campaigns/Customers cards) for a Producer
  // on a direct /home load, before features settled and the page swapped to Pulse.
  const accountResolving = !account || accountFetching;
  const featuresResolving = featuresLoading || !features;
  const isResolving = accountResolving || featuresResolving;

  if (isResolving) {
    // Neutral, full-bleed placeholder (brand background) — no bounce, no body.
    return <View style={styles.root} testID="home-dashboard-loading" />;
  }

  // Pulse self-guard (3-condition, mirrors OEM): Pulse Home flag + Crop Circle
  // + Producer workspace → the Pulse body.
  if (
    isCropCircleEnabled &&
    isPulseHomeDashboardEnabled &&
    workspace?.type === WorkspaceType.Producer
  ) {
    return <PulseDashboard />;
  }

  // OEM self-guard (3-condition): OEM Home flag + Crop Circle + OEM workspace.
  if (
    isCropCircleEnabled &&
    isOemHomeDashboardEnabled &&
    workspace?.type === WorkspaceType.Oem
  ) {
    return <OemDashboard />;
  }

  // Neither a valid OEM nor a valid Pulse combination (flag off / wrong type)
  // → bounce to /activities.
  return <Navigate to="/activities" replace />;
};

/**
 * OemDashboard — the OEM Home Dashboard body (design: gripp-os HomeOEM).
 *
 * Layout (solid header bar, full-canvas map below; the filter panel opens beside
 * the map, shrinking it to half-width — same split as the Pulse dashboard):
 *  - `home-dashboard-topbar` — solid white header: left = TimeRangeSelector,
 *    right = FilterBadge + expandable client-side search.
 *  - `home-dashboard-map` (absoluteFill) — ActivityMap. Plots clustered ASSET
 *    points at rest; switches to the selected card's per-entity points when a
 *    plotting mode is active. `searchQuery` filters the visible markers by name.
 *  - `home-dashboard-cardstrip` (absolute bottom overlay) — the 6-card strip.
 *
 * Filter/plot state lives in `useHomeDashboardFilters` (shared with Pulse); this
 * component owns only `range` (the time selector). The OEM dashboard has no
 * People card, so the hook's person facet stays unused here.
 */
const OemDashboard = () => {
  const { t } = useTranslation();

  const [range, setRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const f = useHomeDashboardFilters();

  return (
    <View style={styles.root} testID="home-dashboard-page">
      {/* Left column — header + map + card strip. Shrinks to share the row with
          the filter panel (rightColumn) when it opens. */}
      <View style={styles.leftColumn}>
        {/* Header bar — solid white chrome holding the controls. */}
        <View style={styles.header} testID="home-dashboard-topbar">
          <TimeRangeSelector range={range} onChange={setRange} />
          <View style={styles.headerControls}>
            <FilterBadge
              count={f.activeFilterCount}
              onPress={() => f.setFilterOpen(true)}
              label={t('home.filters.label', 'Filters')}
            />
            <SearchControl
              value={f.searchQuery}
              onChange={f.setSearchQuery}
              placeholder={t('home.search.placeholder', 'Search')}
            />
          </View>
        </View>

        {/* Map + bottom card overlay fill the area below the header. */}
        <View style={styles.body}>
          <View
            style={styles.mapSlot}
            testID="home-dashboard-map"
            accessibilityLabel={t('home.map.label', 'Activity map')}
          >
            <ActivityMap
              range={range}
              plotMode={f.effectivePlotMode}
              statusFilter={f.effectiveStatusFilter}
              searchQuery={f.searchQuery}
              // Filter-panel facets (multi): groups narrow the asset layer;
              // labels + severity buckets + issue types narrow the activity layer
              // (threaded server-side via the home hooks → HomeActivityFilter).
              groupIds={f.groupIds}
              labelIds={f.labelIds}
              severityBuckets={f.severityBuckets}
              issueTypes={f.issueTypes}
            />
          </View>

          {/* Active-plot indicator (clears the mode + filter) — sits above the
              map. Shows the plotted entity and, when a chart item is selected,
              the sub-filter ("Assets · Out of Service"). */}
          {f.effectivePlotMode && (
            <View style={styles.plotBannerWrap} pointerEvents="box-none">
              <Pressable
                style={styles.plotBanner}
                onPress={f.clearPlot}
                accessibilityRole="button"
                accessibilityLabel={t('home.plot.clear', 'Clear map plotting')}
                testID="home-dashboard-plot-clear"
              >
                <Text style={styles.plotBannerText}>
                  {t(
                    `home.plot.${f.effectivePlotMode.toLowerCase()}`,
                    f.effectivePlotMode
                  )}
                  {/* Activity layer: summarise the multi facets; other layers
                      show their single client-side sub-filter. */}
                  {f.isActivityLayer
                    ? f.activityTypes.length + f.labelIds.length > 0
                      ? ` · ${f.activityTypes.length + f.labelIds.length} ${t(
                          'home.plot.filters',
                          'filters'
                        )}`
                      : ''
                    : f.effectiveStatusFilter
                    ? ` · ${f.effectiveStatusFilter}`
                    : ''}
                </Text>
                <Text style={styles.plotBannerClose}>✕</Text>
              </Pressable>
            </View>
          )}

          <View style={styles.cardStripWrap} pointerEvents="box-none">
            <CardStrip
              range={range}
              plotMode={f.effectivePlotMode}
              statusFilter={f.cardStatusFilter}
              onSelectPlot={f.handleSelectPlot}
              onSelectStatus={f.handleSelectStatus}
            />
          </View>
        </View>
      </View>

      {/* Right column — the filter panel opens to half the screen, shrinking the
          map (left column) to the other half. Toggled by the FilterBadge. */}
      {f.filterOpen && (
        <View style={styles.rightColumn}>
          <PulseFilterPanel
            categories={f.filterCategories}
            selections={f.filterSelections}
            activeCount={f.activeFilterCount}
            onSelect={f.handleSelectFilter}
            onReset={f.clearFilters}
            onClose={() => f.setFilterOpen(false)}
          />
        </View>
      )}
    </View>
  );
};

/**
 * SearchControl — the header's collapsed search button that expands to the Crop
 * Circle `SearchInput` (design: gripp-os HomePage collapsed search). SearchInput
 * owns the search icon, clear button, and web outline suppression, so this only
 * has to manage the collapsed↔expanded toggle: pressing the icon opens + focuses
 * the field; blurring it while empty collapses back to the icon.
 */
const SearchControl = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  if (!open && !value) {
    return (
      <Pressable
        style={[styles.searchButton, hovered && styles.searchButtonHovered]}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={placeholder}
        testID="home-dashboard-search"
      >
        <SearchIcon size={20} color={tokens.colors.neutral[800]} />
      </Pressable>
    );
  }

  return (
    <SearchInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      autoFocus
      height={36}
      width={220}
      onBlur={() => {
        if (!value) setOpen(false);
      }}
      testID="home-dashboard-search-field"
    />
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: tokens.colors.brand.neutral,
    overflow: 'hidden',
  },
  // Dashboard column — full width on its own, shrinks to share the row with the
  // filter panel (rightColumn) when it opens.
  leftColumn: {
    flex: 1,
    overflow: 'hidden',
  },
  // Filter panel column — half the screen, so the map (leftColumn) shrinks to
  // the other half rather than being covered.
  rightColumn: {
    width: '50%',
  },
  // Right-side header controls (filter badge + search), grouped so they sit
  // together at the end of the header row.
  headerControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  // Solid white header chrome (design: --card on a --border-section divider).
  header: {
    height: tokens.spacing['7xl'], // 56px
    paddingHorizontal: tokens.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.brand.white,
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.slate[100],
    zIndex: 10,
  },
  body: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapSlot: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tokens.colors.brand.neutral,
  },
  searchButton: {
    width: 36,
    height: 36,
    borderRadius: tokens.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchButtonHovered: {
    backgroundColor: tokens.colors.neutral[200],
  },
  plotBannerWrap: {
    position: 'absolute',
    top: tokens.spacing.md,
    left: tokens.spacing.lg,
    zIndex: 10,
  },
  plotBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    backgroundColor: tokens.colors.neutral[800],
  },
  plotBannerText: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.brand.white,
  },
  plotBannerClose: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  cardStripWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
});
